import { supabase, isSupabaseConfigured } from '../lib/supabase.js';
import { getCached, setCache } from '../lib/cache.js';
import { createPlayer, renderFrame } from './playback.js';
import { showToast } from '../editor/ui.js';
import { loadAnimationWithKeyframes, rpcToLegacyProject } from '../lib/storage.js';

let currentPlayer = null;

export function initViewer(animationId) {
  const page = document.getElementById('page-viewer');
  const display = document.getElementById('viewer-display');
  const controls = document.getElementById('viewer-controls');

  // Clean up previous player
  if (currentPlayer) {
    currentPlayer.destroy();
    currentPlayer = null;
  }

  display.textContent = 'Loading...';
  display.style.color = '#557';
  controls.innerHTML = '';

  loadAnimation(animationId);
}

async function loadAnimation(animationId) {
  const display = document.getElementById('viewer-display');
  const controls = document.getElementById('viewer-controls');

  // Try cache
  const cached = getCached('anim_' + animationId);
  if (cached) {
    setupViewer(cached, display, controls);
    return;
  }

  if (!isSupabaseConfigured()) {
    display.textContent = 'Supabase not configured';
    display.style.fontSize = '14px';
    return;
  }

  try {
    const rpc = await loadAnimationWithKeyframes(animationId);
    if (!rpc) throw new Error('not found');
    const legacy = rpcToLegacyProject(rpc);

    setCache('anim_' + animationId, legacy, 300000); // 5 min cache

    // Increment view count (fire and forget)
    supabase.rpc('increment_view_count', { p_animation_id: animationId }).catch(() => {});

    setupViewer(legacy, display, controls);
  } catch (e) {
    console.warn('Failed to load animation', animationId, e);
    display.textContent = 'Animation not found';
    display.style.color = '#557';
    display.style.fontSize = '14px';
  }
}

function setupViewer(animData, display, controls) {
  const project = animData.project;
  if (!project || !project.keyframes || !project.sequences) {
    display.textContent = 'Invalid animation data';
    return;
  }

  const seq = project.sequences[0];
  if (!seq || !seq.steps || seq.steps.length === 0) {
    display.textContent = 'Empty animation';
    return;
  }

  const fontSize = project.fontSize || 48;
  display.style.color = '';
  display.style.fontSize = fontSize + 'px';
  if (project.backgroundColor) display.style.backgroundColor = project.backgroundColor;

  // Create player
  currentPlayer = createPlayer(project.keyframes, seq, display, fontSize, project.backgroundColor);

  // Render controls
  renderViewerControls(controls, animData, seq);

  // Auto-play
  currentPlayer.play();
}

function renderViewerControls(container, animData, seq) {
  container.innerHTML = '';

  // Title
  if (animData.title) {
    const title = document.createElement('div');
    title.className = 'viewer-title';
    title.textContent = animData.title;
    container.appendChild(title);
  }

  // Playback controls row
  const row = document.createElement('div');
  row.className = 'viewer-control-row';

  // Play/Pause
  const playBtn = document.createElement('button');
  playBtn.className = 'viewer-btn';
  playBtn.textContent = 'Pause';
  playBtn.addEventListener('click', () => {
    currentPlayer.toggle();
    playBtn.textContent = currentPlayer.isPlaying() ? 'Pause' : 'Play';
  });
  row.appendChild(playBtn);

  // Speed
  const speedLabel = document.createElement('label');
  speedLabel.className = 'viewer-label';
  speedLabel.textContent = 'Speed: ';
  const speedSelect = document.createElement('select');
  speedSelect.className = 'viewer-select';
  [0.5, 1, 1.5, 2, 4].forEach(s => {
    const opt = document.createElement('option');
    opt.value = s;
    opt.textContent = s + 'x';
    if (s === 1) opt.selected = true;
    speedSelect.appendChild(opt);
  });
  speedSelect.addEventListener('change', () => {
    currentPlayer.setSpeed(parseFloat(speedSelect.value));
  });
  speedLabel.appendChild(speedSelect);
  row.appendChild(speedLabel);

  container.appendChild(row);

  // Actions row
  const actions = document.createElement('div');
  actions.className = 'viewer-control-row';

  // Share
  const shareBtn = document.createElement('button');
  shareBtn.className = 'viewer-btn';
  shareBtn.textContent = 'Copy Link';
  shareBtn.addEventListener('click', () => {
    const url = window.location.origin + '/#/view/' + animData.id;
    navigator.clipboard.writeText(url).then(() => showToast('Link copied!'));
  });
  actions.appendChild(shareBtn);

  // Open in Editor (clone to localStorage)
  const editBtn = document.createElement('button');
  editBtn.className = 'viewer-btn viewer-btn-accent';
  editBtn.textContent = 'Open in Editor';
  editBtn.addEventListener('click', () => {
    const project = animData.project;
    localStorage.setItem('unicode-animator-project', JSON.stringify(project));
    window.location.hash = '/editor';
    showToast('Animation loaded into editor');
  });
  actions.appendChild(editBtn);

  container.appendChild(actions);
}

export function cleanupViewer() {
  if (currentPlayer) {
    currentPlayer.destroy();
    currentPlayer = null;
  }
}
